<script lang="ts">
  import { X } from 'lucide-svelte';
  import { createProduct } from '$lib/api/product';
  import { productTypesQuery, categoriesQuery } from '$lib/queries/products';
  import { invalidateAfterProductsUpdate } from '$lib/state/invalidation';
  import { showError, showSuccess } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import type { ProductResponse } from '$lib/types/product';

  // Add a genuinely-missing product. It is created in ProQuote (the master catalog),
  // pending review for pricing, and mirrored back here immediately so it is usable now.
  let {
    productType = null,
    onsuccess = (_p: ProductResponse) => {},
    oncancel = () => {}
  }: {
    productType?: number | null;
    onsuccess?: (product: ProductResponse) => void;
    oncancel?: () => void;
  } = $props();

  const productTypes = productTypesQuery();
  const categories = categoriesQuery();

  let selectedType = $state<number | null>(productType);
  let name = $state('');
  let description = $state('');
  let saving = $state(false);

  // Group types under their category for an easy categorised picker.
  const grouped = $derived.by(() => {
    const cats = categories.data ?? [];
    const types = productTypes.data ?? [];
    return cats
      .map((c) => ({
        category: c.name,
        types: types.filter((t) => t.category_name === c.name)
      }))
      .filter((g) => g.types.length > 0);
  });

  async function submit(e: Event) {
    e.preventDefault();
    if (!selectedType || !name.trim() || saving) return;
    saving = true;
    try {
      const product = await createProduct({
        product_type: selectedType,
        name: name.trim(),
        description: description.trim()
      });
      invalidateAfterProductsUpdate();
      showSuccess(`"${product.name}" added — pending review in ProQuote`);
      onsuccess(product);
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      saving = false;
    }
  }
</script>

<form onsubmit={submit} class="flex flex-col gap-4">
  <p class="text-xs text-text-secondary">
    Only add a product if you've checked it isn't already in the catalogue. New products are
    created in ProQuote and reviewed/priced there.
  </p>

  <div class="flex flex-col gap-1">
    <label for="add-type" class="text-sm font-medium text-text-secondary">Category &amp; type</label>
    <select
      id="add-type"
      bind:value={selectedType}
      required
      class="w-full px-3 py-2 border border-border-strong rounded-lg"
    >
      <option value={null} disabled>Select a type…</option>
      {#each grouped as group}
        <optgroup label={group.category}>
          {#each group.types as t}
            <option value={t.id}>{t.name}</option>
          {/each}
        </optgroup>
      {/each}
    </select>
  </div>

  <div class="flex flex-col gap-1">
    <label for="add-name" class="text-sm font-medium text-text-secondary">Name</label>
    <input
      id="add-name"
      type="text"
      bind:value={name}
      required
      placeholder="e.g. Philips 7W warm-white downlight"
      class="w-full px-3 py-2 border border-border-strong rounded-lg"
    />
  </div>

  <div class="flex flex-col gap-1">
    <label for="add-desc" class="text-sm font-medium text-text-secondary">Description (optional)</label>
    <textarea
      id="add-desc"
      bind:value={description}
      rows="2"
      class="w-full px-3 py-2 border border-border-strong rounded-lg"
    ></textarea>
  </div>

  <div class="flex justify-end gap-2">
    <button
      type="button"
      onclick={oncancel}
      class="px-4 py-2 bg-bg-hover hover:bg-bg-hover text-red-600 rounded-lg flex items-center gap-1"
    >
      <X class="w-5 h-5" />
      Cancel
    </button>
    <button
      type="submit"
      disabled={!selectedType || !name.trim() || saving}
      class="px-4 py-2 bg-accent-primary hover:opacity-90 text-white rounded-lg disabled:opacity-50"
    >
      {saving ? 'Adding…' : 'Add to catalogue'}
    </button>
  </div>
</form>
